import { useEffect, useState, useMemo, ReactNode } from 'react';
import { AuthContext, EMPTY_SCOPE } from './AuthContext';
import Keycloak from 'keycloak-js';
import { IUser } from 'interfaces/User';
import apiInstance from 'interceptors/axiosInterceptor';
import KeycloakService from 'security/KeycloakService';

interface AuthProviderProps {
	children: ReactNode;
	keycloak: Keycloak;
}

export const AuthProvider = ({ children, keycloak }: AuthProviderProps) => {
	const [user, setUser] = useState<IUser | undefined>(undefined);
	const [loading, setLoading] = useState<boolean>(true);

	useEffect(() => {
		const syncProfile = async () => {
			setLoading(true);
			if (keycloak.authenticated) {
				try {
					const response = await apiInstance.get('/users/profile');
					const userData: IUser = response.data;
					// El perfil trae la dependencia como objeto; la planilla y las novedades leen su id plano
					setUser({
						...userData,
						patrimonial_dependence_id: userData.patrimonial_dependence?.id ?? userData.patrimonial_dependence_id
					});
				} catch (error) {
					console.error('Error syncing profile:', error);
					setUser(undefined);
				} finally {
					setLoading(false);
				}
			} else {
				setUser(undefined);
				setLoading(false);
			}
		};

		syncProfile();
	}, [keycloak.authenticated, keycloak.subject]);

	// El alcance lo decide el backend (tabla rol → alcance/capacidades) y llega en el perfil. Acá no se vuelve a
	// deducir del nombre del rol: sin perfil o con un rol desconocido el alcance es `none` y no se entra.
	const scope = useMemo(() => user?.scope ?? EMPTY_SCOPE, [user]);

	const isAuthorized = useMemo(() => {
		if (loading) return false;
		if (!user) return false;

		// Entrar al sistema depende de tener un rol reconocido, no del alcance patrimonial: el Director de
		// Informática no alcanza ninguna dependencia y entra igual, porque su menú es la gestión de usuarios.
		const roleName = user.role?.name ?? KeycloakService.getUserRoles()[0];
		if (!roleName) return false;

		// Un alcance acotado sin dependencia asignada no puede operar sobre nada: es un usuario a medio configurar.
		if (scope.mode === 'assigned') return scope.dependencies.length > 0 || !!user.patrimonial_dependence;

		return true;
	}, [user, loading, scope]);

	// Una sola fuente del rol para la autorización, el menú y las pantallas: el backend lo resuelve de los grupos del
	// token y lo devuelve en el perfil; el token solo cubre el caso en que el perfil aún no llegó.
	const roles = useMemo(() => (user?.role?.name ? [user.role.name] : KeycloakService.getUserRoles()), [user]);

	const logout = () => {
		KeycloakService.logout();
	};

	return (
		<AuthContext.Provider value={{ keycloak, user, loading, isAuthorized, roles, scope, logout }}>
			{children}
		</AuthContext.Provider>
	);
};
